import * as React from "react";
import { styled, Box, Collapse, Typography, Switch, IconButton, Stack } from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";

export interface PermissionAccordionItemProps {
  expanded: boolean;
  onClickExpand: () => void;
  title: string;
  searchValue: string;
  children: { label: string; value: AclTypes }[];
  activeValues: AclTypes[];
  setAcls: React.Dispatch<React.SetStateAction<AclTypes[]>>;
}

const PermissionAccordionItem: React.FC<PermissionAccordionItemProps> = (props) => {
  const { title, searchValue, expanded, children, onClickExpand, activeValues, setAcls } = props;
  const filteredMode = props.searchValue.length > 0;

  const onChangeSwitch = (item: AclTypes) => {
    if (activeValues.includes(item)) setAcls(activeValues.filter((x) => x !== item));
    else setAcls([...activeValues, item]);
  };

  const onChangeAll = (checked: boolean) => {
    let acls: AclTypes[] = [];
    if (checked) {
      children.forEach((child) => {
        if (!activeValues.includes(child.value)) acls.push(child.value);
      });
      setAcls([...activeValues, ...acls]);
    } else setAcls(activeValues.filter((active) => !children.map((child) => child.value).includes(active)));
  };

  return (
    <StyledListItem>
      <StyledHeader expanded={expanded} onClick={onClickExpand}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton>
            <ExpandMoreRoundedIcon
              sx={{ color: "text.tier", transform: expanded ? "rotate(-180deg)" : "rotate(-90deg)" }}
              fontSize="small"
            />
          </IconButton>
          <Typography fontWeight={500}>{title}</Typography>
        </Stack>
        <Switch size="small" disabled={filteredMode} onChange={(e, checked) => onChangeAll(checked)} />
      </StyledHeader>
      <Collapse in={expanded}>
        <ul>
          {children
            .filter(
              (x) =>
                !filteredMode ||
                x.label.includesAllWords(searchValue) ||
                x.value.toString().includesAllWords(searchValue)
            )
            .map((item) => (
              <StyledPermission slot="li" key={item.value}>
                <span>{item.label}</span>{" "}
                <Stack direction="row" alignItems="center" spacing={3} ml={3}>
                  <ErrorOutlineOutlinedIcon fontSize="small" sx={{ color: "text.tier" }} />
                  <Switch
                    size="small"
                    checked={activeValues.includes(item.value)}
                    onChange={() => onChangeSwitch(item.value)}
                  />
                </Stack>
              </StyledPermission>
            ))}
        </ul>
      </Collapse>
    </StyledListItem>
  );
};

export default PermissionAccordionItem;

const StyledListItem = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  overflow: "hidden",
  "&:first-of-type": {
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  },
  "&:last-of-type": {
    borderBottomLeftRadius: 6,
    borderBottomRightRadius: 6,
  },
  borderTop: "none",
}));
const StyledHeader = styled(Box, { shouldForwardProp: (prop) => prop !== "expanded" })<{ expanded?: boolean }>(
  ({ theme, expanded }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: `${theme.spacing(1.5)} ${theme.spacing(3)} ${theme.spacing(1.5)} ${theme.spacing(1.5)}`,
    transition: "background .3s",
    background: expanded ? theme.palette.backgroundAlpha.pressed : "transparent",
    cursor: "pointer",
    "&:hover": {
      background: expanded ? theme.palette.backgroundAlpha.pressed : theme.palette.backgroundAlpha.hover,
    },
  })
);

const StyledPermission = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  width: "100%",
  padding: `${theme.spacing(2.5)} ${theme.spacing(3)} ${theme.spacing(2.5)} ${theme.spacing(6)}`,
  background: theme.palette.backgroundAlpha.main,
  borderBottom: `1px solid ${theme.palette.backgroundAlpha.border}`,
  "&:last-of-type": {
    borderBottom: "none",
  },
}));
